import React, { FunctionComponent, useEffect, useState } from "react";
import { Backdrop } from "@mui/material";
import { CustomLoadingIndicator } from "../../components/CustomLoadingIndicator";
import styles from "./styles.module.scss";
import queryString from "query-string";
import { RoutesBuilder } from "../../models/RoutesBuilder";
import { useNavigate } from "react-router";
import { useProcessMercadoPagoSubscription } from "../../hooks/mercadopago/useProcessMercadoPagoSubscription";
import { StatusCodesHelper } from "../../models/StatusCodesHelper";
import { strings } from "../../localization/LocalizedStrings";
import { AlertDialog } from "../../components/AlertDialog";
import { useSessionManager } from "../../hooks/session";
import { LoginAgainDialog } from "../../components/LoginAgainDialog";
import { StatusCodes } from "http-status-codes";
import { useCreateMercadoPagoPurchase } from "../../hooks/mercadopago/useCreateMercadoPagoPurchase";
import { Firebase } from "../../services/Firebase";

export const PaymentPage: FunctionComponent = () => {
  useEffect(() => {
    document.title = strings.app_name;
  }, []);
  const navigate = useNavigate();
  const { closeSession } = useSessionManager();
  const { processMercadoPagoSubscription } = useProcessMercadoPagoSubscription();
  const { createMercadoPagoPurchase } = useCreateMercadoPagoPurchase();
  const [isSignInAgainDialogOpen, setIsSignInAgainDialogOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [shouldCloseSession, setShouldCloseSession] = useState(false);
  const processPayment = () => {
    const queryParams = queryString.parse(location.search);
    const subscriptionId = queryParams.preapproval_id;
    if (typeof subscriptionId != "string") {
      processPurchase();
    } else {
      processPaymentFromMercadoPago(subscriptionId).then();
    }
  };
  const processPurchase = () => {
    const queryParams = queryString.parse(location.search);
    const paymentId = queryParams.payment_id;
    if (typeof paymentId != "string") {
      navigate(RoutesBuilder.home());
    } else {
      createPurchaseFromMercadoPago(paymentId).then();
    }
  };
  useEffect(() => {
    processPayment();
  }, []);
  const processPaymentFromMercadoPago = async (subscriptionId: string) => {
    setShouldCloseSession(true);
    const { status, body } = await processMercadoPagoSubscription({
      externalSubscriptionId: subscriptionId
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(strings.subscription_success);
      Firebase.shared.logFirebaseEvent("payment_subscription", {
        externalSubscriptionId: subscriptionId
      });
    } else if (status == StatusCodes.FORBIDDEN) {
      setIsSignInAgainDialogOpen(true);
    } else {
      setAlertMessage(body.message);
    }
    setIsAlertOpen(true);
  };
  const createPurchaseFromMercadoPago = async (paymentId: string) => {
    setShouldCloseSession(false);
    const { status, body } = await createMercadoPagoPurchase({
      paymentId: paymentId
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(strings.purchase_success);
      Firebase.shared.logFirebaseEvent("payment_purchase", { paymentId: paymentId });
    } else if (status == StatusCodes.FORBIDDEN) {
      setIsSignInAgainDialogOpen(true);
    } else {
      setAlertMessage(body.message);
    }
    setIsAlertOpen(true);
  };
  return (
    <Backdrop className={styles.backdrop} open={true}>
      <CustomLoadingIndicator />
      <LoginAgainDialog
        isOpen={isSignInAgainDialogOpen}
        setIsOpen={setIsSignInAgainDialogOpen}
        closeOnTouch={false}
        loginCallback={async () => {
          setIsSignInAgainDialogOpen(false);
          processPayment();
        }}
      />
      <AlertDialog
        title={strings.app_name}
        message={alertMessage}
        open={isAlertOpen}
        setOpen={setIsAlertOpen}
        onConfirm={() => {
          if (shouldCloseSession) {
            closeSession();
          }
          navigate(RoutesBuilder.home());
        }}
      />
    </Backdrop>
  );
};
